import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import employeeService from '../../services/EmployeeService';
import toast from 'react-hot-toast';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  warningIcon: {
    color: 'orange'
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  orText: {
    fontWeight: 'bold',
    textAlign: 'center'
  },
  formControlLabel: {
    '& .MuiFormControlLabel-label': {
      fontWeight: 500,
      fontSize: '1rem'
    }
  }
}));

const AlertDialog = ({
  handleClose,
  open,
  loading,
  handleContinue,
  selectedEmployees,
  setSelectedEmployees,
  allEmployeeChecked,
  setAllEmployeeChecked
}) => {
  const classes = useStyles();
  const [activeEmployeesOptions, setActiveEmployeesOptions] = useState([]);

  const handleChange = (event) => {
    setAllEmployeeChecked(event.target.checked);
    setSelectedEmployees([]);
  };

  useEffect(() => {
    const fetchActiveEmployees = async () => {
      try {
        const activeEmployeesResponse = await employeeService.getActiveEmployees();

        const activeEmployeesOptions = activeEmployeesResponse.data.data.map((employee) => {
          return { value: employee._id, label: employee.DisplayName };
        });

        setActiveEmployeesOptions(activeEmployeesOptions);
      } catch (error) {
        toast.error(error?.response?.data?.message || error?.message || 'Could not fetch active employees record');
      }
    };

    fetchActiveEmployees();
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" disableTypography>
        <Typography variant="h6" className={classes.dialogTitle}>
          <WarningIcon className={classes.warningIcon} />
          Default Mandatory Qualifications
        </Typography>
      </DialogTitle>

      <DialogContent>
        <div className={classes.dialogContent}>
          <FormControlLabel
            control={
              <Checkbox checked={allEmployeeChecked} onChange={handleChange} color="primary" name="allEmployee" />
            }
            label="Apply default mandatory qualifications to all employees?"
            className={classes.formControlLabel}
          />

          <Typography className={classes.orText}>OR</Typography>

          <div>
            <Typography variant="body1" gutterBottom>
              Select employees to apply default qualifications:
            </Typography>

            <Select
              isMulti
              name="selectedEmployees"
              options={activeEmployeesOptions}
              className="basic-multi-select"
              value={selectedEmployees}
              onChange={setSelectedEmployees}
              classNamePrefix="select"
              // eslint-disable-next-line no-undef
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              isDisabled={allEmployeeChecked}
            />
          </div>
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="primary" variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={() => handleContinue({ allEmployeeChecked, selectedEmployees })}
          disabled={loading}
          color="primary"
          variant="contained"
          autoFocus
        >
          {loading ? 'Saving...' : 'Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlertDialog;

AlertDialog.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  handleContinue: PropTypes.func.isRequired,
  selectedEmployees: PropTypes.arrayOf(PropTypes.object).isRequired,
  setSelectedEmployees: PropTypes.func.isRequired,
  allEmployeeChecked: PropTypes.bool.isRequired,
  setAllEmployeeChecked: PropTypes.func.isRequired
};
